import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LaunchOfferCard, LaunchPlanStatus } from "@/components/LaunchOffer";
import { Loader2, Users, DoorClosed, CreditCard, CalendarClock, Settings, Sparkles, CheckCircle2, AlertTriangle, Clock, Percent } from "lucide-react";

const STATUS_STYLE = {
  trialing: "bg-blue-100 text-blue-700",
  active: "bg-emerald-100 text-emerald-700",
  past_due: "bg-amber-100 text-amber-800",
  canceled: "bg-rose-100 text-rose-700",
};

export default function Billing() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["billing"],
    queryFn: async () => (await api.get("/billing/summary")).data,
  });

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const manage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const status = data.subscription_status;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold">{t("Billing & Subscription")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("${{cleaner}} per cleaner + ${{room}} per room each week (${{vol}} for cleaners beyond {{c}} and rooms beyond {{r}}) — billed monthly. Usage updates automatically.", { cleaner: data.rate_cleaner ?? 1, room: data.rate_room ?? 1, vol: data.volume_rate, c: data.tier1_cleaners, r: data.tier1_rooms })}
        </p>
        {data.property_code && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-1.5" data-testid="billing-property-id">
            <span className="label-caps text-muted-foreground">{t("Property ID")}</span>
            <span className="font-mono text-base font-bold tracking-wider">{data.property_code}</span>
            <span className="text-xs text-muted-foreground">{t("— your staff use this to sign in")}</span>
          </div>
        )}
      </div>

      <LaunchPlanStatus plan={data.launch_plan} overflow={data.launch_overflow} />
      {data.launch_offer_eligible && <LaunchOfferCard />}

      {/* status banner */}
      <Card className="flex flex-col gap-4 rounded-md border p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary">
            <Sparkles className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display text-lg font-bold">{t("KlenaPro Subscription")}</span>
              <Badge className={`capitalize ${STATUS_STYLE[status] || "bg-secondary text-foreground"}`} data-testid="subscription-status">
                {t(status?.replace("_", " "))}
              </Badge>
            </div>
            {status === "trialing" && data.trial_days_left != null && (
              <div className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground">
                <CalendarClock className="h-3.5 w-3.5" />
                {t("{{count}} days left in free trial", { count: data.trial_days_left })}
              </div>
            )}
            <AccountStatusLine access={data.access} discount={data.discount} contact={data.contact} status={status} />
          </div>
        </div>
        <div className="flex gap-2">
          {data.has_subscription ? (
            <Button onClick={manage} disabled={busy} data-testid="manage-subscription-btn" variant="outline" className="k-press gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />}
              {t("Manage subscription")}
            </Button>
          ) : (
            <Button onClick={subscribe} disabled={busy} data-testid="subscribe-btn" className="k-press gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              {status === "trialing" ? t("Add payment method") : t("Subscribe now")}
            </Button>
          )}
        </div>
      </Card>

      {/* usage + cost */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile icon={Users} label={t("Active Cleaners")} value={data.cleaners} sub={`$${Number(data.weekly_cleaners ?? 0).toFixed(2)}/wk`} />
        <Tile icon={DoorClosed} label={t("Rooms")} value={data.rooms} sub={`$${Number(data.weekly_rooms ?? 0).toFixed(2)}/wk`} />
        <Tile icon={CreditCard} label={t("Weekly Cost")} value={`$${data.weekly_cost}`} sub={t("cleaners + rooms")} />
        <Tile
          icon={CalendarClock}
          label={t("Est. Monthly")}
          value={`$${data.discount ? data.monthly_effective : data.monthly_estimate}`}
          sub={data.discount ? t("was ${{was}} · {{percent}}% off", { was: data.monthly_estimate, percent: data.discount.percent }) : t("billed monthly")}
          highlight
        />
      </div>

      <BillingBreakdown />

      <Card className="rounded-md border p-5">
        <div className="label-caps mb-3 text-muted-foreground">{t("How billing works")}</div>
        <ul className="space-y-2 text-sm">
          {[
            t("Every active cleaner and every active room counts as one billing unit. Disabled cleaners and rooms are only billed while they still have work recorded in the last 30 days."),
            t("Cleaners are ${{cleaner}}/week and rooms are ${{room}}/week, charged monthly. Volume pricing: every cleaner beyond {{c}} and every room beyond {{r}} is just ${{vol}}/week.", { cleaner: data.rate_cleaner ?? 1, room: data.rate_room ?? 1, c: data.tier1_cleaners, r: data.tier1_rooms, vol: data.volume_rate }),
            data.rate_locked
              ? t("Your rates are locked for the life of your subscription — future price changes won't affect you while it stays active.")
              : t("Your rates lock in when your paid subscription starts and stay fixed while it remains active."),
            t("Add or remove cleaners and rooms anytime — your subscription quantity updates automatically."),
            t("Your first 14 days are free. You won't be charged until the trial ends."),
          ].map((line) => (
            <li key={line} className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function AccountStatusLine({ access, discount, contact, status }) {
  const { t } = useTranslation();
  const rows = [];
  if (access?.reason === "disabled") {
    rows.push({ key: "disabled", icon: AlertTriangle, cls: "text-destructive",
      text: t("Account disabled — view only. Contact KlenaPro on {{contact}}.", { contact }) });
  } else if (access?.reason === "nonpayment") {
    rows.push({ key: "nonpayment", icon: AlertTriangle, cls: "text-destructive",
      text: t("Locked for non-payment — view only. Contact KlenaPro on {{contact}}.", { contact }) });
  } else if (access?.reason === "grace") {
    rows.push({ key: "grace", icon: Clock, cls: "text-amber-600 font-semibold",
      text: t("Payment overdue — {{count}} days until account is disabled.", { count: access.days_left }) });
  } else if (status === "active") {
    rows.push({ key: "active", icon: CheckCircle2, cls: "text-emerald-600",
      text: t("Account active") });
  }
  if (discount) {
    rows.push({ key: "discount", icon: Percent, cls: "text-emerald-600 font-semibold",
      text: t("{{percent}}% discount", { percent: discount.percent }) + " " + (discount.until ? t("until {{date}}", { date: discount.until.slice(0, 10) }) : t("(ongoing)")) });
  }
  if (rows.length === 0) return null;
  return (
    <div className="mt-0.5 space-y-0.5" data-testid="account-status-line">
      {rows.map((r) => {
        const Icon = r.icon;
        return (
          <div key={r.key} className={`flex items-center gap-1 text-sm ${r.cls}`}>
            <Icon className="h-3.5 w-3.5 shrink-0" /> {r.text}
          </div>
        );
      })}
    </div>
  );
}


function Tile({ icon: Icon, label, value, sub, highlight }) {
  return (
    <Card className={`rounded-md border p-4 ${highlight ? "bg-primary text-primary-foreground" : ""}`}>
      <div className={`flex items-center gap-2 ${highlight ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
        <Icon className="h-4 w-4" />
        <span className="label-caps">{label}</span>
      </div>
      <div className="mt-2 font-display text-2xl font-extrabold">{value}</div>
      <div className={`text-xs ${highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{sub}</div>
    </Card>
  );
}

const REASON = {
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
  disabled_with_data: { label: "Disabled · worked in last 30 days", cls: "bg-amber-100 text-amber-800" },
  disabled_no_data: { label: "Disabled · not billed", cls: "bg-slate-100 text-slate-500" },
};

function BillingBreakdown() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["billing-breakdown"],
    queryFn: async () => (await api.get("/billing/breakdown")).data,
    enabled: open,
  });
  const Row = ({ name, reason, testid }) => (
    <li className="flex items-center justify-between gap-2 py-1 text-sm" data-testid={testid}>
      <span className={reason === "disabled_no_data" ? "text-muted-foreground line-through" : ""}>{name}</span>
      <Badge className={`font-normal ${REASON[reason].cls}`}>{t(REASON[reason].label)}</Badge>
    </li>
  );
  return (
    <Card className="rounded-md border p-5" data-testid="billing-breakdown">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left" data-testid="billing-breakdown-toggle">
        <span className="label-caps text-muted-foreground">{t("What's being billed this month")}</span>
        <span className="text-xs text-muted-foreground">{open ? t("Hide") : t("Show")}</span>
      </button>
      {open && data && (
        <div className="mt-4 grid gap-6 md:grid-cols-2">
          <div>
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4" /> {t("Cleaners")} · {data.cleaners.filter((c) => c.billed).length}/{data.cleaners.length} {t("billed")}</div>
            <ul className="divide-y">{data.cleaners.map((c) => <Row key={c.id} name={c.name} reason={c.reason} testid={`bill-cleaner-${c.id}`} />)}</ul>
          </div>
          <div>
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold"><DoorClosed className="h-4 w-4" /> {t("Rooms")} · {data.rooms.filter((r) => r.billed).length}/{data.rooms.length} {t("billed")}</div>
            <ul className="divide-y">{data.rooms.map((r) => <Row key={r.room_number} name={`${t("Room")} ${r.room_number}`} reason={r.reason} testid={`bill-room-${r.room_number}`} />)}</ul>
          </div>
          <p className="text-xs text-muted-foreground md:col-span-2">{t("Window: work recorded since {{date}}. Disabled cleaners and rooms drop off automatically once they have no work in the last {{days}} days.", { date: data.since, days: data.window_days })}</p>
        </div>
      )}
    </Card>
  );
}
